import Konva from 'konva';
import type { ComponentDef } from '../../core/library/component-def.js';
import { renderPinLabel } from './pin-label.js';
import { addUnifiedValueLabel as unifiedLabel } from './value-label.js';

/**
 * Renderer del cuerpo de un LED visto DE PERFIL con silueta tipo "bala":
 * cilindro con la parte superior redondeada como una cúpula.
 * El color se elige por propiedad de instancia "color".
 */

const LED_COLORS: Record<string, string> = {
  red: '#e53935',
  green: '#43a047',
  orange: '#fb8c00',
  blue: '#1e88e5',
  white: '#f5f5f5',
};

export function renderLedBody(
  def: ComponentDef,
  labelText: string,
  color: string,
  rotationDeg: number = 0,
  flipped: boolean = false,
): Konva.Group {
  const g = new Konva.Group();

  const width = def.body.length_mm ?? 5.08;
  const height = def.body.width_mm ?? 7.62;
  const bodyColor = LED_COLORS[color] ?? LED_COLORS['green']!;
  const light = mix(bodyColor, '#ffffff', 0.55);
  const dark = mix(bodyColor, '#000000', 0.35);
  const topRadius = Math.min(width / 2, height / 2);

  // Cuerpo bala: rect con esquinas superiores completamente redondeadas.
  const rect = new Konva.Rect({
    x: -width / 2,
    y: -height / 2,
    width,
    height,
    cornerRadius: [topRadius, topRadius, 0, 0],
    fillLinearGradientStartPoint: { x: -width / 2, y: 0 },
    fillLinearGradientEndPoint: { x: width / 2, y: 0 },
    fillLinearGradientColorStops: [
      0, dark,
      0.4, bodyColor,
      0.55, light,
      1, dark,
    ],
    stroke: dark,
    strokeWidth: 0.08,
    listening: true,
  });
  rect.setAttr('nodeKind', 'body');
  g.add(rect);

  // Reborde de polaridad: pequeña brida horizontal en la parte
  // baja del cuerpo que SOBRESALE 0.5 mm por el lado IZQUIERDO
  // (ánodo) y coincide con el borde derecho (cátodo). Emula la
  // muesca / lengüeta plástica que marca la polaridad en un LED
  // real (visto de perfil).
  const flangeH = 0.5;
  const flangeExt = 0.5;
  g.add(
    new Konva.Rect({
      x: -width / 2 - flangeExt,
      y: height / 2 - flangeH,
      width: width + flangeExt,
      height: flangeH,
      fillLinearGradientStartPoint: { x: -width / 2 - flangeExt, y: 0 },
      fillLinearGradientEndPoint: { x: width / 2, y: 0 },
      fillLinearGradientColorStops: [
        0, dark,
        0.35, bodyColor,
        0.55, light,
        1, dark,
      ],
      stroke: dark,
      strokeWidth: 0.08,
      listening: false,
    }),
  );

  // Reflejo brillante en la cúpula.
  g.add(
    new Konva.Ellipse({
      x: -width * 0.18,
      y: -height / 2 + topRadius * 0.5,
      radiusX: width * 0.18,
      radiusY: topRadius * 0.3,
      fill: '#ffffff',
      opacity: 0.55,
      listening: false,
    }),
  );

  // Etiqueta del valor (con estilo unificado: fondo blanco + texto negro).
  if (labelText) {
    const ps = flipped ? -1 : 1;
    const labelGroup = new Konva.Group({
      rotation: ps * (0 - rotationDeg),
      scaleY: ps,
      listening: false,
    });
    const fontSize = def.appearance.label?.font_size_mm ?? Math.min(width, height) * 0.28;
    unifiedLabel(labelGroup, labelText, fontSize, width * 0.9);
    g.add(labelGroup);
  }

  // Etiquetas A / K junto a cada pin, fuera del cuerpo cerca de éste.
  const pinFont = Math.min(width * 0.28, 1.2);
  for (const pin of def.pins) {
    g.add(renderPinLabel(pin, pin.id, 1.4, rotationDeg, flipped, pinFont));
  }

  return g;
}

function mix(a: string, b: string, t: number): string {
  const ca = parseHex(a);
  const cb = parseHex(b);
  return `#${toHex(ca.r + (cb.r - ca.r) * t)}${toHex(ca.g + (cb.g - ca.g) * t)}${toHex(ca.b + (cb.b - ca.b) * t)}`;
}
function parseHex(hex: string): { r: number; g: number; b: number } {
  let s = hex.replace('#', '');
  if (s.length === 3) s = s.split('').map((c) => c + c).join('');
  return {
    r: parseInt(s.slice(0, 2), 16),
    g: parseInt(s.slice(2, 4), 16),
    b: parseInt(s.slice(4, 6), 16),
  };
}
function toHex(n: number): string {
  const v = Math.round(Math.max(0, Math.min(255, n)));
  return v.toString(16).padStart(2, '0');
}
